import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ResponsiveContainer, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts'
import { Upload, X } from 'lucide-react'
import { API_ENDPOINTS } from '../config/api'
import { useAuth } from '../components/AuthProvider'
import { useI18n } from '../i18n'
import { PageLayout } from '../layouts/PageLayout'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'
import { SearchField } from '../components/ui/SearchField'
import styles from '../styles/pages/Reports.module.css'
import { TableEmptyRow, TableLoadingRow } from '../components/governance/TableBodyStates'
import { FilePicker } from '../components/ui/FilePicker'
import ReportSummaryPosturePanel from '../components/reports/ReportSummaryPosturePanel'
import ReportsEnterpriseSection, { ReportsEnterpriseCard } from '../components/reports/ReportsEnterpriseSection'
import FilteredVulnerabilitiesList from '../components/reports/FilteredVulnerabilitiesList'
import ScannerAssessmentsWorkspace from '../components/reports/ScannerAssessmentsWorkspace'
import ReportsViewNav from '../components/reports/ReportsViewNav'
import {
  REPORTS_CHART_AXIS,
  REPORTS_CHART_GRID,
  reportsChartAxisStroke,
  reportsChartTick,
} from '../components/reports/reportsChartTheme'
import { featureFlags } from '../config/featureFlags'
import { formatScannerSources, formatScannerSourceLabel } from '../services/vulnerabilityPostureApi'
import entStyles from '../styles/enterprise/EnterpriseOperationalUx.module.scss'
import {
  buildReportsViewHref,
  resolveReportsView,
  type ReportsView,
} from './reports/reportsView'

type SeverityKey = 'critical' | 'high' | 'medium' | 'low' | 'info'

interface ReportOption {
  id: number
  name: string
  status?: string | null
  createdAt?: string | null
  reportType?: string | null
  sourceType?: 'UPLOADED' | 'SCANNER' | 'UNIFIED'
  target?: string | null
  uniqueFindings?: number | null
  organizationName?: string | null
  organization?: {
    id: number | string
    name: string
  } | null
}

interface SummaryAsset {
  id: number | string
  name: string
  type: string
  os?: string | null
  uniqueFindings?: number
  vulnerabilityCount: number
  severity: Record<SeverityKey, number>
}

interface SummaryVulnerability {
  id: number
  name: string
  severity: string
  cvss?: number | null
  cve?: string | null
  asset?: string | null
  status?: string | null
  lastDetectedAt?: string | null
  firstDetectedAt?: string | null
  detectedBy?: string[]
  sourceProvider?: string | null
  assessmentCount?: number
}

interface SummaryData {
  reportId: number
  organizationId: number
  name: string
  scanner: string | null
  lastScan: string | null
  riskScore: number
  securityHealth: number
  critical: number
  high: number
  medium: number
  low: number
  info: number
  total: number
  totals: {
    assets: number
    vulnerabilities: number
    assetsWithVulnerabilities?: number
    linkedInformationSystems?: number
  }
  severity: Record<SeverityKey, number>
  assets: SummaryAsset[]
  vulnerabilities: SummaryVulnerability[]
  summary: Record<string, unknown>
  metadata: {
    scanStartTime?: string | null
    scanEndTime?: string | null
    taskName?: string | null
    taskId?: string | null
    scanProfile?: string | null
    engineVersion?: string | null
    scanStatus?: string | null
  }
  stats?: StatsData | null
  sources?: string[]
  unlinkedFindingsCount?: number
}

interface StatsData {
  reportId: number
  totals: {
    assets: number
    vulnerabilities: number
    hosts: number
    services: number
    vulnerabilitiesPerHost: number | Record<string, unknown>
  }
  severityDistribution: {
    name: string
    key: SeverityKey
    count: number
  }[]
  familyDistribution: {
    family: string | null
    count: number
  }[]
  falsePositives: number
  exploitable: number
}

interface AssetsResponse {
  items: SummaryAsset[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

interface VulnerabilityItem {
  id: number
  name: string
  title?: string
  severity: string
  riskLevel: string
  cvss?: number | null
  cve?: string | null
  asset?: string | null
  assetId?: number | null
  description?: string | null
  recommendations?: string | null
  isFalsePositive: boolean
  isExploitable: boolean
  status: string
  firstDetectedAt?: string | null
  lastDetectedAt?: string | null
  detectedBy?: string[] | string
  sourceProvider?: string | null
  normalizedFingerprint?: string | null
  assetKey?: string | null
  assessmentCount?: number
  affectedInstancesCount?: number
  instances?: string[]
}

interface VulnerabilitiesResponse {
  items: VulnerabilityItem[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

interface RecentAssessmentRow {
  id: string
  scannerScanId?: string
  finishedAt?: string | null
  providerType?: string
  scanner?: string
  target?: string | null
  status?: string
  uniqueFindings?: number
}

type VulnStateFilter =
  | 'current'
  | 'open'
  | 'resolved'
  | 'historical'
  | 'stale'
  | 'false_positive'
  | 'all'

const SEVERITY_COLORS: Record<SeverityKey, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#facc15',
  low: '#3b82f6',
  info: '#9ca3af',
}

function healthScoreColor(percent: number): string {
  const p = Math.max(0, Math.min(100, percent))
  if (p >= 75) return '#22c55e'
  if (p >= 50) return '#eab308'
  if (p >= 25) return '#f97316'
  return '#ef4444'
}

const SCANNER_OPTIONS = ['OpenVAS', 'Nessus', 'Qualys', 'Generic', 'Other']

const pageSize = 20

const formatNumber = (value: number | undefined | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '0'
  return value.toLocaleString()
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—'
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleString()
  } catch {
    return value
  }
}

const formatDateShort = (value: string | null | undefined) => {
  if (!value) return '—'
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return '—'
  }
}

type ReportStatusTone = 'success' | 'warn' | 'danger' | 'muted'

function severityBadgeClass(severity: string) {
  const s = String(severity || '').toUpperCase()
  if (s === 'CRITICAL') return entStyles.badgeRisk
  if (s === 'HIGH') return entStyles.badgeWarn
  if (s === 'MEDIUM') return entStyles.badge
  return entStyles.badgeStable
}

function reportStatusTone(status: string | null | undefined): ReportStatusTone {
  const s = (status || '').toLowerCase()
  if (['completed', 'complete', 'ready', 'success', 'processed', 'done', 'ok'].some((x) => s.includes(x))) {
    return 'success'
  }
  if (['fail', 'error'].some((x) => s.includes(x))) return 'danger'
  if (['process', 'pending', 'upload', 'pars', 'queue'].some((x) => s.includes(x))) return 'warn'
  return 'muted'
}

function vulnerabilitiesPaginationIsFirst(page: number) {
  return page <= 1
}

function vulnerabilitiesPaginationIsLast(page: number, totalPages: number) {
  return page >= totalPages
}

const Reports: React.FC = () => {
  const { t } = useI18n()
  const { token, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const view: ReportsView = resolveReportsView(searchParams)

  const [uploadedReports, setUploadedReports] = useState<ReportOption[]>([])
  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [stats, setStats] = useState<StatsData | null>(null)
  const [assets, setAssets] = useState<AssetsResponse | null>(null)
  const [vulnerabilities, setVulnerabilities] = useState<VulnerabilitiesResponse | null>(null)
  const [recentAssessments, setRecentAssessments] = useState<RecentAssessmentRow[]>([])

  const [listLoading, setListLoading] = useState(false)
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [loadingVulns, setLoadingVulns] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initialSeverity = (searchParams.get('severity') || 'all').toLowerCase()
  const [severityFilter, setSeverityFilter] = useState<SeverityKey | 'all'>(
    ['critical', 'high', 'medium', 'low', 'info'].includes(initialSeverity)
      ? (initialSeverity as SeverityKey)
      : 'all',
  )
  const [vulnStateFilter, setVulnStateFilter] = useState<VulnStateFilter>(
    (searchParams.get('state') as VulnStateFilter) || 'current',
  )
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [vulnDateFilter, setVulnDateFilter] = useState<'all' | '7d' | '15d' | '30d'>(
    (searchParams.get('date') as 'all' | '7d' | '15d' | '30d') || 'all',
  )
  const [vulnerabilitiesPage, setVulnerabilitiesPage] = useState(1)

  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadScanner, setUploadScanner] = useState('Generic')
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing'>('idle')
  const [uploadError, setUploadError] = useState<string | null>(null)

  const [organizationName, setOrganizationName] = useState<string>('—')
  const [deletingReportId, setDeletingReportId] = useState<number | null>(null)

  const scanIdFromUrl = searchParams.get('scanId') || searchParams.get('scannerScanId')
  const assetIdFromUrl = searchParams.get('assetId')
  const assetFilterFromUrl = searchParams.get('asset') || assetIdFromUrl || ''
  const [assetFilter, setAssetFilter] = useState(assetFilterFromUrl)

  // Legacy redirect: tab present without view → replace with resolved view
  useEffect(() => {
    const hasTab = searchParams.has('tab')
    const hasView = searchParams.has('view')
    if (!hasTab || hasView) return

    const resolved = resolveReportsView(searchParams)
    const next = new URLSearchParams()
    next.set('view', resolved)

    for (const key of ['scanId', 'scannerScanId', 'severity', 'state', 'assetId', 'asset', 'date', 'search']) {
      const v = searchParams.get(key)
      if (v) next.set(key, v)
    }

    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Sync findings filters into durable URL params
  useEffect(() => {
    if (view !== 'findings') return
    const next = new URLSearchParams(searchParams)
    next.set('view', 'findings')
    next.set('state', vulnStateFilter === 'open' ? 'current' : vulnStateFilter)
    if (severityFilter !== 'all') next.set('severity', severityFilter)
    else next.delete('severity')
    if (vulnDateFilter !== 'all') next.set('date', vulnDateFilter)
    else next.delete('date')
    if (searchTerm.trim()) next.set('search', searchTerm.trim())
    else next.delete('search')
    if (assetFilter.trim()) {
      next.set('asset', assetFilter.trim())
      // Prefer canonical `asset`; keep assetId only when it looks like an id
      if (assetIdFromUrl && assetFilter.trim() === assetIdFromUrl) next.set('assetId', assetIdFromUrl)
      else next.delete('assetId')
    } else {
      next.delete('asset')
      next.delete('assetId')
    }
    next.delete('tab')
    next.delete('section')
    const cur = searchParams.toString()
    const nxt = next.toString()
    if (cur !== nxt) setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, vulnStateFilter, severityFilter, vulnDateFilter, searchTerm, assetFilter])

  // Keep asset filter in sync when navigating via chart / deep links
  useEffect(() => {
    const fromUrl = searchParams.get('asset') || searchParams.get('assetId') || ''
    if (fromUrl !== assetFilter) setAssetFilter(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const primaryOrganization = useMemo(() => {
    if (!user) return null
    const explicitId = (user as any)?.organizationId || (user as any)?.organization_id || (user as any)?.orgId
    if (explicitId) {
      return { id: String(explicitId), name: (user as any)?.organizationName || '' }
    }
    if (Array.isArray(user.organizations) && user.organizations.length > 0) {
      const entry = user.organizations[0]
      if (typeof entry === 'object' && entry) {
        return { id: entry.id ? String(entry.id) : '', name: entry.name || '' }
      }
      if (typeof entry === 'string') {
        return { id: entry, name: entry }
      }
    }
    return null
  }, [user])

  const fetchJson = useCallback(async (url: string, init: RequestInit = {}) => {
    if (!token) throw new Error('Missing token')
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...(init.headers as Record<string, string> | undefined),
    }
    const response = await fetch(url, { ...init, headers })
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      throw new Error(message || `Request failed (${response.status})`)
    }
    return response.json()
  }, [token])

  const fetchUploadedReports = useCallback(async () => {
    if (!token) return
    if (!primaryOrganization?.id) {
      setUploadedReports([])
      return
    }
    setListLoading(true)
    try {
      const data = await fetchJson(API_ENDPOINTS.ORG_REPORTS_LIST(primaryOrganization.id))
      const fromApi: ReportOption[] = Array.isArray(data?.reports)
        ? data.reports
            .map((report: any) => ({
              id: Number(report.id ?? report.reportId ?? 0),
              name: report.name || report.reportName || `Report #${report.id}`,
              status: report.status || null,
              createdAt: report.createdAt || report.uploadedAt || null,
              reportType: report.reportType || report.scanner || null,
              sourceType: report.sourceType || 'UPLOADED',
              target: report.target || report.assetName || null,
              uniqueFindings:
                report.uniqueFindingsCount ??
                report.uniqueFindings ??
                report.findingsCount ??
                null,
              organizationName: report.organizationName || report.organization?.name || null,
              organization: report.organization || null,
            }))
            .filter(
              (r: ReportOption) =>
                r.sourceType === 'UPLOADED' &&
                typeof r.id === 'number' &&
                Number.isFinite(r.id) &&
                r.id > 0,
            )
        : []

      setUploadedReports(fromApi)
      if (fromApi[0]?.organizationName) {
        setOrganizationName(fromApi[0].organizationName)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reports list')
    } finally {
      setListLoading(false)
    }
  }, [fetchJson, primaryOrganization?.id, token])

  const loadOverview = useCallback(async () => {
    if (!token) return
    setOverviewLoading(true)
    setError(null)
    try {
      const [analytics, assessmentsData] = await Promise.all([
        fetchJson(API_ENDPOINTS.REPORTS_UNIFIED_ANALYTICS),
        fetchJson(`${API_ENDPOINTS.REPORTS_ASSESSMENTS}?limit=5`).catch(() => ({ items: [] })),
      ])
      setSummary(analytics)
      setStats(analytics.stats ?? null)
      const topAssets = Array.isArray(analytics.assets) ? analytics.assets : []
      setAssets({
        items: topAssets,
        pagination: {
          page: 1,
          limit: Math.max(10, topAssets.length),
          total: topAssets.length,
          totalPages: 1,
        },
      })
      const items = Array.isArray(assessmentsData?.items) ? assessmentsData.items : []
      const sorted = [...items].sort((a: RecentAssessmentRow, b: RecentAssessmentRow) => {
        const tb = a.finishedAt ? new Date(a.finishedAt).getTime() : 0
        const ta = b.finishedAt ? new Date(b.finishedAt).getTime() : 0
        const nb = Number.isFinite(tb) ? tb : 0
        const na = Number.isFinite(ta) ? ta : 0
        // Newest assessments first (Overview "Recent assessments")
        if (na !== nb) return na - nb
        return String(b.id || b.scannerScanId || '').localeCompare(String(a.id || a.scannerScanId || ''))
      })
      setRecentAssessments(sorted.slice(0, 5))
    } catch (err: any) {
      setError(err.message || 'Failed to load unified report data')
    } finally {
      setOverviewLoading(false)
    }
  }, [fetchJson, token])

  const loadVulnerabilities = useCallback(
    async (page: number, severity: SeverityKey | 'all', search: string, state: VulnStateFilter = 'current', asset = '') => {
      setLoadingVulns(true)
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
          state: state === 'open' ? 'current' : state,
        })
        if (severity !== 'all') params.set('severity', severity.toUpperCase())
        if (search.trim()) params.set('search', search.trim())
        if (asset.trim()) params.set('asset', asset.trim())
        if (vulnDateFilter !== 'all') {
          const days = vulnDateFilter === '7d' ? 7 : vulnDateFilter === '15d' ? 15 : 30
          const from = new Date(Date.now() - days * 86400000)
          params.set('dateFrom', from.toISOString())
        }
        const data = await fetchJson(`${API_ENDPOINTS.REPORTS_VULNERABILITIES}?${params.toString()}`)
        setVulnerabilities(data)
      } catch (err: any) {
        setError(err.message || 'Failed to load vulnerabilities')
      } finally {
        setLoadingVulns(false)
      }
    },
    [fetchJson, vulnDateFilter],
  )

  const pollReportStatus = useCallback(
    async (reportId: number) => {
      const maxAttempts = 30
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const status = await fetchJson(API_ENDPOINTS.REPORT_STATUS(reportId))
          if (status.status === 'completed') {
            return true
          }
          if (status.status === 'failed') {
            throw new Error('Report parsing failed')
          }
        } catch (err: any) {
          throw new Error(err.message || 'Failed to poll status')
        }
        await new Promise((resolve) => setTimeout(resolve, 2500))
      }
      throw new Error('Report parsing is taking longer than expected')
    },
    [fetchJson],
  )

  const resetUploadModal = useCallback(() => {
    setShowUploadModal(false)
    setUploadFile(null)
    setUploadStatus('idle')
    setUploadError(null)
  }, [])

  const handleUploadReport = useCallback(async () => {
    if (!uploadFile) return
    if (!primaryOrganization?.id) {
      setUploadError(t('reports.messages.organizationNotFound', 'Organization not found'))
      return
    }
    setUploadError(null)
    setUploadStatus('uploading')
    try {
      const form = new FormData()
      form.append('file', uploadFile)
      form.append('scannerType', uploadScanner)
      const response = await fetch(API_ENDPOINTS.ORG_REPORTS_UPLOAD(primaryOrganization.id), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}` || '',
        },
        body: form,
      })
      if (!response.ok) {
        const message = await response.text().catch(() => '')
        throw new Error(message || 'Upload failed')
      }
      const payload = await response.json()
      const newReportId = Number(payload.reportId ?? payload.document_id)
      if (Number.isFinite(newReportId)) {
        setUploadStatus('processing')
        await pollReportStatus(newReportId)
        await fetchUploadedReports()
        resetUploadModal()
      } else {
        throw new Error('Invalid report identifier')
      }
    } catch (err: any) {
      setUploadStatus('idle')
      setUploadError(err.message || 'Failed to upload report')
    }
  }, [
    fetchUploadedReports,
    pollReportStatus,
    primaryOrganization?.id,
    resetUploadModal,
    t,
    token,
    uploadFile,
    uploadScanner,
  ])

  const handleDeleteReport = useCallback(
    async (reportId: number, reportName: string) => {
      if (!token || !primaryOrganization?.id) return
      const confirmed = window.confirm(
        t(
          'reports.deleteConfirm',
          'Delete report "{name}" from the platform and remove its stored file if present?',
        ).replace('{name}', reportName),
      )
      if (!confirmed) return
      setDeletingReportId(reportId)
      setError(null)
      try {
        const response = await fetch(
          API_ENDPOINTS.ORG_REPORTS_DELETE(primaryOrganization.id, reportId),
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
        )
        if (!response.ok) {
          const message = await response.text().catch(() => '')
          throw new Error(message || `Delete failed (${response.status})`)
        }
        await fetchUploadedReports()
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to delete report'
        setError(message)
      } finally {
        setDeletingReportId(null)
      }
    },
    [fetchUploadedReports, primaryOrganization?.id, t, token],
  )

  const handleExportPdf = useCallback(
    async (reportId: number) => {
      if (!token) return
      try {
        const response = await fetch(API_ENDPOINTS.REPORT_PDF(reportId), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) {
          const message = await response.text().catch(() => '')
          throw new Error(message || `PDF export failed (${response.status})`)
        }
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `report-${reportId}.pdf`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        URL.revokeObjectURL(url)
      } catch (err: any) {
        setError(err.message || 'Failed to export PDF')
      }
    },
    [token],
  )

  const handleScanIdChange = useCallback(
    (scanId: string | null) => {
      const next = new URLSearchParams(searchParams)
      next.set('view', 'assessments')
      if (scanId) next.set('scanId', scanId)
      else {
        next.delete('scanId')
        next.delete('scannerScanId')
      }
      next.delete('tab')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    if (primaryOrganization?.name) {
      setOrganizationName(primaryOrganization.name)
    } else if ((user as any)?.organizationName) {
      setOrganizationName((user as any).organizationName)
    }
  }, [primaryOrganization, user])

  const refreshCurrentFindingsPosture = useCallback(() => {
    loadOverview().catch(() => undefined)
    if (view === 'findings') {
      loadVulnerabilities(
        vulnerabilitiesPage,
        severityFilter,
        searchTerm,
        vulnStateFilter,
        assetFilter,
      ).catch(() => undefined)
    }
  }, [
    loadOverview,
    loadVulnerabilities,
    view,
    vulnerabilitiesPage,
    severityFilter,
    searchTerm,
    vulnStateFilter,
    assetFilter,
  ])

  useEffect(() => {
    if (!token) return
    if (view === 'overview') {
      loadOverview().catch(() => undefined)
    }
  }, [loadOverview, token, view])

  useEffect(() => {
    if (!token) return
    if (view === 'uploads') {
      fetchUploadedReports().catch(() => undefined)
    }
  }, [fetchUploadedReports, token, view])

  useEffect(() => {
    if (view !== 'findings') return
    loadVulnerabilities(vulnerabilitiesPage, severityFilter, searchTerm, vulnStateFilter, assetFilter).catch(
      () => undefined,
    )
  }, [
    loadVulnerabilities,
    searchTerm,
    severityFilter,
    vulnerabilitiesPage,
    vulnStateFilter,
    vulnDateFilter,
    assetFilter,
    view,
  ])

  useEffect(() => {
    setVulnerabilitiesPage(1)
  }, [severityFilter, searchTerm, vulnStateFilter, vulnDateFilter, assetFilter])

  useEffect(() => {
    if (!showUploadModal) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetUploadModal()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [showUploadModal, resetUploadModal])

  const vulnerabilitySeverityOptions = useMemo(
    () =>
      [
        { value: 'all' as const, label: t('reports.filters.risk.all', 'All severities') },
        { value: 'critical' as const, label: t('severity.critical', 'Critical') },
        { value: 'high' as const, label: t('severity.high', 'High') },
        { value: 'medium' as const, label: t('severity.medium', 'Medium') },
        { value: 'low' as const, label: t('severity.low', 'Low') },
        { value: 'info' as const, label: t('severity.info', 'Info') },
      ] as { value: SeverityKey | 'all'; label: string }[],
    [t],
  )

  const activityDateFilterOptions = useMemo(
    () => [
      { value: '7d' as const, label: t('reports.filters.last7', 'Last 7 days') },
      { value: '15d' as const, label: t('reports.filters.last15', 'Last 15 days') },
      { value: '30d' as const, label: t('reports.filters.last30', 'Last 30 days') },
      { value: 'all' as const, label: t('reports.filters.allTime', 'All time') },
    ],
    [t],
  )

  const vulnStateFilterOptions = useMemo(
    () =>
      [
        { value: 'current' as const, label: t('reports.filters.state.current', 'Current / Open') },
        { value: 'resolved' as const, label: t('reports.filters.state.resolved', 'Resolved') },
        { value: 'historical' as const, label: t('reports.filters.state.historical', 'Historical') },
        { value: 'stale' as const, label: t('reports.filters.state.stale', 'Stale') },
        { value: 'false_positive' as const, label: t('reports.filters.state.falsePositive', 'False Positive') },
        { value: 'all' as const, label: t('reports.filters.state.all', 'All') },
      ] as { value: VulnStateFilter; label: string }[],
    [t],
  )

  const uploadScannerOptions = useMemo(
    () => SCANNER_OPTIONS.map((name) => ({ value: name, label: name })),
    [],
  )

  const severityChartData = useMemo(() => {
    if (!stats) return []
    return stats.severityDistribution.map((item) => ({
      name: t(`severity.${item.key}`, item.name),
      count: item.count ?? 0,
      key: item.key,
    }))
  }, [stats, t])

  const assetChartData = useMemo(() => {
    const rows = assets?.items?.length ? assets.items : summary?.assets || []
    return rows.map((a) => ({
      ...a,
      // Same unit as Filtered / Home: current unique findings
      vulnerabilityCount: Number(a.uniqueFindings ?? a.vulnerabilityCount ?? 0),
      uniqueFindings: Number(a.uniqueFindings ?? a.vulnerabilityCount ?? 0),
    }))
  }, [assets, summary?.assets])

  const openFindingsForAsset = useCallback(
    (asset: { id?: number | string; name?: string }) => {
      // Prefer exact hostname/name over UUID so Filtered uses strict host match
      // (avoids pulling sibling TLDs via shared mislinked assetId).
      const name = String(asset?.name || '').trim()
      const id = asset?.id != null ? String(asset.id) : ''
      const key = name || id
      if (!key) return
      setAssetFilter(key)
      setVulnerabilitiesPage(1)
      navigate(buildReportsViewHref('findings', searchParams, { asset: key, state: 'current' }))
    },
    [navigate, searchParams],
  )

  const vulnerabilityItems = vulnerabilities?.items || []
  const totalVulnerabilityPages = vulnerabilities?.pagination?.totalPages || 1

  const statusBadgeToneClass: Record<ReportStatusTone, string> = {
    success: styles.badgeSuccess,
    warn: styles.badgeWarn,
    danger: styles.badgeDanger,
    muted: styles.badgeMuted,
  }

  if (!token) {
    return <div className="p-6 text-sm text-gray-500">{t('reports.messages.unauthorized', 'Unauthorized access')}</div>
  }

  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1

  const filteredVulnerabilityRows = vulnerabilityItems.map((item) => ({
    id: item.id,
    name: item.name || item.title || 'Finding',
    severity: item.severity,
    cvss: item.cvss,
    cve: item.cve,
    asset: item.asset,
    status: item.status,
    firstDetectedAt: item.firstDetectedAt,
    lastDetectedAt: item.lastDetectedAt,
    detectedBy: Array.isArray(item.detectedBy)
      ? item.detectedBy
      : item.sourceProvider
        ? String(item.sourceProvider).split(',').map((s) => s.trim())
        : [],
    sourceProvider: item.sourceProvider,
    normalizedFingerprint: item.normalizedFingerprint,
    assetKey: item.assetKey,
    assessmentCount: item.assessmentCount,
    affectedInstancesCount:
      item.affectedInstancesCount ??
      (Array.isArray(item.instances) ? item.instances.length : 1),
    instances: item.instances || [],
  }))

  const renderSummaryAnalytics = () => {
    if (!summary) {
      if (overviewLoading) {
        return <p className={styles.emptyHint}>{t('common.loading', 'Loading...')}</p>
      }
      return null
    }

    const chartPanel = (title: string, chart: React.ReactNode) =>
      enterpriseUx ? (
        <ReportsEnterpriseCard title={title}>{chart}</ReportsEnterpriseCard>
      ) : (
        <div className={styles.chartPanel}>
          <h3 className={styles.chartPanelTitle}>{title}</h3>
          <div className={styles.chartPanelBody}>{chart}</div>
        </div>
      )

    const severityChart = (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={severityChartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={enterpriseUx ? REPORTS_CHART_GRID : 'rgba(15, 23, 42, 0.08)'}
          />
          <XAxis
            dataKey="name"
            tick={enterpriseUx ? reportsChartTick : { fontSize: 11 }}
            stroke={enterpriseUx ? reportsChartAxisStroke : 'var(--ds-text-secondary, #6b7280)'}
          />
          <YAxis
            tick={enterpriseUx ? reportsChartTick : { fontSize: 11 }}
            allowDecimals={false}
            stroke={enterpriseUx ? reportsChartAxisStroke : 'var(--ds-text-secondary, #6b7280)'}
          />
          <Tooltip
            contentStyle={
              enterpriseUx
                ? {
                    background: '#fff',
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    borderRadius: 8,
                    color: '#0f172a',
                  }
                : undefined
            }
          />
          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
            {severityChartData.map((item, index) => (
              <Cell key={`sev-${index}`} fill={SEVERITY_COLORS[item.key]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )

    const assetsChart = (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={assetChartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={enterpriseUx ? REPORTS_CHART_GRID : 'rgba(15, 23, 42, 0.08)'}
          />
          <XAxis
            dataKey="name"
            tick={enterpriseUx ? reportsChartTick : { fontSize: 11 }}
            stroke={enterpriseUx ? reportsChartAxisStroke : 'var(--ds-text-secondary, #6b7280)'}
          />
          <YAxis
            allowDecimals={false}
            tick={enterpriseUx ? reportsChartTick : { fontSize: 11 }}
            stroke={enterpriseUx ? reportsChartAxisStroke : 'var(--ds-text-secondary, #6b7280)'}
          />
          <Tooltip
            contentStyle={
              enterpriseUx
                ? {
                    background: '#fff',
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    borderRadius: 8,
                    color: '#0f172a',
                  }
                : undefined
            }
          />
          <Legend wrapperStyle={enterpriseUx ? { color: REPORTS_CHART_AXIS } : undefined} />
          <Bar
            dataKey="uniqueFindings"
            fill="var(--ds-primary, #56a3d9)"
            name={t('reports.uniqueFindings', 'Unique findings')}
            radius={[6, 6, 0, 0]}
            cursor="pointer"
            onClick={(data: any) => {
              const payload = data?.payload || data
              if (payload) openFindingsForAsset(payload)
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    )

    const chartsBlock = (
      <div className={enterpriseUx ? styles.enterpriseChartsRow : styles.chartsRow}>
        {chartPanel(t('reports.severityDistribution', 'Severity distribution'), (
          <div
            className={enterpriseUx ? styles.enterpriseChartBody : styles.chartPanelBody}
          >
            {severityChart}
          </div>
        ))}
        {chartPanel(t('reports.topAssets', 'Most vulnerable assets'), (
          <div
            className={enterpriseUx ? styles.enterpriseChartBody : styles.chartPanelBody}
          >
            {assetsChart}
            {(!summary.assets || summary.assets.length === 0) &&
            Number(summary.totals?.assetsWithVulnerabilities ?? 0) > 0 ? (
              <p className={styles.emptyHint}>
                {t('reports.unlinkedFindings', 'Unlinked findings')}:{' '}
                {Number(summary.unlinkedFindingsCount ?? 0)}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    )

    const keyVulnerabilities = (summary.vulnerabilities || [])
      .filter((item) => {
        const sev = (item.severity || '').toUpperCase()
        return sev === 'CRITICAL' || sev === 'HIGH' || sev === 'MEDIUM'
      })
      .slice(0, 10)

    const expectedMedium = Number(summary.severity?.medium ?? summary.medium ?? 0)
    const keyMediumCount = keyVulnerabilities.filter(
      (i) => String(i.severity || '').toUpperCase() === 'MEDIUM',
    ).length

    const formatKeyScanners = (item: SummaryVulnerability) => {
      if (item.detectedBy?.length) {
        return item.detectedBy.map((s) => formatScannerSourceLabel(s)).join(' · ')
      }
      if (item.sourceProvider) {
        return String(item.sourceProvider)
          .split(',')
          .map((s) => formatScannerSourceLabel(s.trim()))
          .join(' · ')
      }
      return '—'
    }

    const formatSeen = (value: string | null | undefined) => {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleDateString()
      } catch {
        return value
      }
    }

    const bottomBlock = (
      <div className={enterpriseUx ? styles.enterpriseBottomRow : styles.summaryBottomRow}>
        {chartPanel(
          t('reports.topVulnerabilities', 'Key vulnerabilities'),
          keyVulnerabilities.length === 0 ? (
            <p className={styles.emptyHint}>{t('common.noData', 'No data available')}</p>
          ) : (
            <div className={styles.vulnCardList}>
              {expectedMedium > 0 ? (
                <p className={styles.emptyHint} style={{ marginBottom: 8 }}>
                  {t('reports.keyVulnsMediumAligned', 'Medium unique findings: {count}', {
                    count: String(expectedMedium),
                  })}
                  {keyMediumCount !== expectedMedium
                    ? ` · ${t('reports.keyVulnsShowingTop', 'showing top {n}', { n: String(keyMediumCount) })}`
                    : ''}
                </p>
              ) : null}
              {keyVulnerabilities.map((item) => (
                <article key={item.id} className={`${entStyles.layer} ${styles.filteredVulnCard}`}>
                  <div className={styles.filteredVulnSummaryMain}>
                    <p className={styles.filteredVulnTitle}>{item.name}</p>
                    <p className={styles.filteredVulnMeta}>
                      {item.asset || '—'}
                      {' · '}
                      <span className={severityBadgeClass(item.severity)}>{item.severity}</span>
                    </p>
                    <p className={styles.filteredVulnMeta}>
                      {t('reports.scanners', 'Scanner(s)')}: {formatKeyScanners(item)}
                    </p>
                    <p className={styles.filteredVulnMeta}>
                      {t('reports.firstSeen', 'First seen')}: {formatSeen(item.firstDetectedAt)}
                      {' · '}
                      {t('reports.detected', 'Last seen')}: {formatSeen(item.lastDetectedAt)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          ),
        )}
      </div>
    )

    const posture = (
      <ReportSummaryPosturePanel
        reportName={summary.name}
        lastScanLabel={formatDate(summary.lastScan)}
        assetsWithVulns={Number(summary.totals?.assetsWithVulnerabilities ?? 0)}
        linkedSystems={Number(summary.totals?.linkedInformationSystems ?? 0)}
        severity={summary.severity}
        securityHealth={Number(summary.securityHealth ?? 0)}
        riskScore={Number(summary.riskScore ?? 0)}
        scanner={formatScannerSources(
          Array.isArray(summary.sources) ? summary.sources : summary.scanner?.split(' + '),
        )}
        unregisteredScannerTargets={
          (summary as { unregisteredScannerTargets?: {
            scopeLabel?: string
            assetTargets?: number
            uniqueFindings?: number
            affectedInstances?: number
            severityDistribution?: Partial<Record<SeverityKey, number>>
            scannerSources?: string[]
          } | null }).unregisteredScannerTargets || null
        }
      />
    )

    if (enterpriseUx) {
      return (
        <>
          {posture}
          <section className={entStyles.layer}>
            <p className={entStyles.layerLabel}>
              {t('reports.analytics.layerLabel', 'Vulnerability analytics')}
            </p>
            {chartsBlock}
          </section>
          <section className={entStyles.layer}>{bottomBlock}</section>
        </>
      )
    }

    return (
      <section className={styles.summarySection} aria-labelledby="report-summary-heading">
        {posture}
        {chartsBlock}
        {bottomBlock}
      </section>
    )
  }

  const renderRecentAssessments = () => {
    const body = (
      <>
        <div className={styles.enterpriseTableWrap}>
          <table className={styles.enterpriseTable}>
            <thead>
              <tr>
                <th className={styles.enterpriseTh}>{t('reports.scanner', 'Scanner')}</th>
                <th className={styles.enterpriseTh}>{t('reports.target', 'Target')}</th>
                <th className={styles.enterpriseTh}>{t('reports.finished', 'Finished')}</th>
                <th className={styles.enterpriseThCenter}>{t('reports.uniqueFindings', 'Unique findings')}</th>
                <th className={styles.enterpriseThCenter}>{t('reports.table.status', 'Status')}</th>
                <th className={styles.enterpriseTh}>{t('reports.table.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {overviewLoading && (
                <TableLoadingRow colSpan={6}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              )}
              {!overviewLoading && recentAssessments.length === 0 && (
                <TableEmptyRow colSpan={6} className={styles.enterpriseEmptyCell}>
                  {t('reports.noAssessments', 'No assessments yet')}
                </TableEmptyRow>
              )}
              {!overviewLoading &&
                recentAssessments.map((row) => {
                  const scanId = row.scannerScanId || row.id
                  return (
                    <tr key={row.id || scanId}>
                      <td className={styles.enterpriseTd}>
                        {formatScannerSourceLabel(row.scanner || row.providerType || '')}
                      </td>
                      <td className={styles.enterpriseTdMuted}>{row.target || '—'}</td>
                      <td className={styles.enterpriseTdMuted}>{formatDate(row.finishedAt)}</td>
                      <td className={styles.enterpriseTdCenter}>
                        {formatNumber(row.uniqueFindings ?? 0)}
                      </td>
                      <td className={styles.enterpriseTdCenter}>{row.status || '—'}</td>
                      <td className={styles.enterpriseTd}>
                        {scanId ? (
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            onClick={() =>
                              navigate(buildReportsViewHref('assessments', searchParams, { scanId }))
                            }
                          >
                            {t('reports.view', 'View')}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => navigate(buildReportsViewHref('assessments', searchParams))}
          >
            {t('reports.viewAllAssessments', 'View all assessments')}
          </Button>
        </div>
      </>
    )

    if (enterpriseUx) {
      return (
        <ReportsEnterpriseSection
          layerLabel={t('reports.recentAssessments.layerLabel', 'Recent assessments')}
          title={t('reports.recentAssessments.title', 'Recent assessments')}
          subtitle={t(
            'reports.recentAssessments.subtitle',
            'Latest scanner assessments across your organization',
          )}
          testId="reports-recent-assessments"
        >
          {body}
        </ReportsEnterpriseSection>
      )
    }

    return (
      <section className="ds-card p-6" style={{ marginTop: '1.5rem' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--ds-text)]">
            {t('reports.recentAssessments.title', 'Recent assessments')}
          </h3>
        </div>
        {body}
      </section>
    )
  }

  const vulnFiltersToolbar = (
    <div className={enterpriseUx ? styles.enterpriseToolbar : styles.filtersToolbar}>
      <SearchField
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={t('reports.filters.searchPlaceholder', 'Search by name or CVE')}
        className={enterpriseUx ? styles.enterpriseToolbarGrow : styles.filtersToolbarGrow}
        minWidth="11rem"
        aria-label={t('reports.filters.searchPlaceholder', 'Search by name or CVE')}
      />
      <Select<VulnStateFilter>
        className="text-sm"
        minWidth="12rem"
        variant={enterpriseUx ? 'outline' : 'default'}
        options={vulnStateFilterOptions}
        value={vulnStateFilter}
        onChange={(v) => v != null && setVulnStateFilter(v)}
        aria-label={t('reports.filters.state.label', 'State')}
      />
      <Select<SeverityKey | 'all'>
        className="text-sm"
        minWidth="11rem"
        variant={enterpriseUx ? 'outline' : 'default'}
        options={vulnerabilitySeverityOptions}
        value={severityFilter}
        onChange={(v) => v != null && setSeverityFilter(v)}
      />
      <Select<'all' | '7d' | '15d' | '30d'>
        className="text-sm"
        minWidth="10rem"
        variant={enterpriseUx ? 'outline' : 'default'}
        options={activityDateFilterOptions}
        value={vulnDateFilter}
        onChange={(v) => v != null && setVulnDateFilter(v)}
      />
      {assetFilter.trim() ? (
        <Button
          type="button"
          size="small"
          color="outline"
          onClick={() => setAssetFilter('')}
          aria-label={t('reports.filters.clearAsset', 'Clear asset filter')}
        >
          {t('reports.asset', 'Asset')}: {assetFilter.length > 28 ? `${assetFilter.slice(0, 28)}…` : assetFilter} ×
        </Button>
      ) : null}
    </div>
  )

  const renderFindings = () => {
    const pagination =
      vulnerabilities && vulnerabilities.pagination.totalPages > 1 ? (
        <div className={enterpriseUx ? styles.enterprisePagination : 'flex items-center justify-between text-xs text-[var(--ds-text-secondary)]'}>
          <span>
            {t('reports.pagination.page', 'Page')} {vulnerabilities.pagination.page}{' '}
            {t('reports.pagination.of', 'of')} {vulnerabilities.pagination.totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={vulnerabilitiesPaginationIsFirst(vulnerabilitiesPage)}
              onClick={() => setVulnerabilitiesPage((prev) => Math.max(1, prev - 1))}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={vulnerabilitiesPaginationIsLast(vulnerabilitiesPage, totalVulnerabilityPages)}
              onClick={() => setVulnerabilitiesPage((prev) => Math.min(totalVulnerabilityPages, prev + 1))}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      ) : null

    if (enterpriseUx) {
      return (
        <ReportsEnterpriseSection
          layerLabel={t('reports.vuln.layerLabel', 'Vulnerability inventory')}
          title={t('reports.vuln.title', 'Filtered vulnerabilities')}
          subtitle={
            <>
              {t(
                'reports.vuln.subtitleCurrent',
                'Current unique findings (same authority as severity cards and Key vulnerabilities).',
              )}
              {' · '}
              {severityFilter !== 'all'
                ? t('reports.vuln.filteredCount', '{count} {severity} unique findings', {
                    count: String(vulnerabilities?.pagination?.total ?? filteredVulnerabilityRows.length),
                    severity: severityFilter,
                  })
                : t('reports.vuln.totalCount', '{count} unique findings', {
                    count: String(vulnerabilities?.pagination?.total ?? filteredVulnerabilityRows.length),
                  })}
            </>
          }
          testId="reports-vuln-enterprise"
        >
          {vulnFiltersToolbar}
          <FilteredVulnerabilitiesList items={filteredVulnerabilityRows} loading={loadingVulns} />
          {pagination}
        </ReportsEnterpriseSection>
      )
    }

    return (
      <section className={`ds-card p-6 ${styles.vulnFiltersSection}`}>
        {vulnFiltersToolbar}
        <FilteredVulnerabilitiesList items={filteredVulnerabilityRows} loading={loadingVulns} />
        {pagination}
      </section>
    )
  }

  const renderUploads = () => {
    const toolbar = (
      <div className={enterpriseUx ? styles.enterpriseRecentActions : styles.toolbarActions}>
        <Button
          type="button"
          size="small"
          color={enterpriseUx ? 'outline' : 'primary'}
          icon={<Upload className="h-4 w-4" aria-hidden />}
          onClick={() => setShowUploadModal(true)}
        >
          {t('reports.upload', 'Upload Report')}
        </Button>
      </div>
    )

    const table = (
      <div className={enterpriseUx ? styles.enterpriseTableWrap : styles.tableWrap}>
        <table className={enterpriseUx ? styles.enterpriseTable : styles.table}>
          <thead>
            <tr>
              <th className={enterpriseUx ? styles.enterpriseTh : styles.th}>
                {t('reports.table.fileName', 'File name')}
              </th>
              <th className={enterpriseUx ? styles.enterpriseTh : styles.th}>
                {t('reports.table.type', 'Type')}
              </th>
              <th className={enterpriseUx ? styles.enterpriseTh : styles.th}>
                {t('reports.table.detectedSource', 'Detected source')}
              </th>
              <th className={enterpriseUx ? styles.enterpriseTh : styles.th}>
                {t('reports.table.uploadedAt', 'Uploaded at')}
              </th>
              <th className={enterpriseUx ? styles.enterpriseTh : styles.th}>
                {t('reports.table.status', 'Status')}
              </th>
              <th className={enterpriseUx ? styles.enterpriseThCenter : styles.th}>
                {t('reports.uniqueFindings', 'Unique findings')}
              </th>
              <th className={enterpriseUx ? styles.enterpriseTh : styles.th}>
                {t('reports.table.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {listLoading && (
              <TableLoadingRow colSpan={7}>{t('common.loading', 'Loading...')}</TableLoadingRow>
            )}
            {!listLoading && uploadedReports.length === 0 && (
              <TableEmptyRow
                colSpan={7}
                className={enterpriseUx ? styles.enterpriseEmptyCell : styles.tableEmptyCell}
              >
                {t('reports.noUploadedReports', 'No uploaded reports')}
              </TableEmptyRow>
            )}
            {!listLoading &&
              uploadedReports.map((report) => {
                const tone = reportStatusTone(report.status)
                const statusText = report.status?.trim() || '—'
                return (
                  <tr key={report.id}>
                    <td className={enterpriseUx ? styles.enterpriseTd : styles.td}>
                      <span className={enterpriseUx ? styles.enterpriseReportName : styles.reportName}>
                        {report.name}
                      </span>
                    </td>
                    <td className={enterpriseUx ? styles.enterpriseTdMuted : styles.td}>
                      {report.reportType || '—'}
                    </td>
                    <td className={enterpriseUx ? styles.enterpriseTdMuted : styles.td}>
                      {t('reports.source.uploadedReport', 'Uploaded report')}
                    </td>
                    <td className={enterpriseUx ? styles.enterpriseTdMuted : `${styles.td} ${styles.dateCell}`}>
                      {formatDateShort(report.createdAt)}
                    </td>
                    <td className={enterpriseUx ? styles.enterpriseTd : styles.td}>
                      <span className={`${styles.badge} ${statusBadgeToneClass[tone]}`}>{statusText}</span>
                    </td>
                    <td className={enterpriseUx ? styles.enterpriseTdCenter : styles.td}>
                      {report.uniqueFindings ?? '—'}
                    </td>
                    <td className={enterpriseUx ? styles.enterpriseTd : `${styles.td} ${styles.actionsCell}`}>
                      <div className={enterpriseUx ? styles.enterpriseRecentActions : undefined}>
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          onClick={() => handleExportPdf(report.id)}
                        >
                          {t('reports.action.exportPdf', 'Export PDF')}
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          disabled={deletingReportId === report.id}
                          onClick={() => handleDeleteReport(report.id, report.name)}
                        >
                          {deletingReportId === report.id
                            ? t('common.deleting', 'Deleting…')
                            : t('reports.action.delete', 'Delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    )

    if (enterpriseUx) {
      return (
        <ReportsEnterpriseSection
          layerLabel={t('reports.uploads.layerLabel', 'Uploaded files')}
          title={t('reports.uploads.title', 'Uploaded Files')}
          subtitle={
            <>
              {t('reports.uploads.subtitle', 'Manually uploaded scanner reports')}
              {organizationName && organizationName !== '—' ? (
                <>
                  <br />
                  {t('reports.organizationTitle', 'Organization: {orgName}', { orgName: organizationName })}
                </>
              ) : null}
            </>
          }
          testId="reports-uploads-enterprise"
        >
          {toolbar}
          {table}
        </ReportsEnterpriseSection>
      )
    }

    return (
      <section className={styles.recentCard} aria-labelledby="reports-uploads-heading">
        <div className={styles.recentCardHeader}>
          <div>
            <h2 id="reports-uploads-heading" className={styles.recentTitle}>
              {t('reports.uploads.title', 'Uploaded Files')}
            </h2>
            <p className={styles.recentSubtitle}>
              {t('reports.uploads.subtitle', 'Manually uploaded scanner reports')}
            </p>
          </div>
          {toolbar}
        </div>
        {table}
      </section>
    )
  }

  return (
    <PageLayout
      pageScope="reports-page"
      title={t('nav.reports', 'Reports')}
      description={t('reports.pageSubtitle', 'View and export your compliance reports')}
    >
      {error && (
        <div className={styles.alertError} role="alert">
          {error}
        </div>
      )}

      <ReportsViewNav
        view={view}
        searchParams={searchParams}
        onNavigate={(href) => navigate(href)}
      />

      <div
        className={
          enterpriseUx
            ? `${entStyles.workspace} ${styles.reportsEnterpriseWorkspace}`
            : styles.reportsLegacyStack
        }
        data-reports-surface="light-page"
      >
        {view === 'overview' && (
          <>
            {renderSummaryAnalytics()}
            {renderRecentAssessments()}
          </>
        )}

        {view === 'findings' && renderFindings()}

        {view === 'assessments' && (
          <ScannerAssessmentsWorkspace
            token={token}
            initialScanId={scanIdFromUrl}
            assetId={assetIdFromUrl}
            onScanIdChange={handleScanIdChange}
            onPostureMaybeChanged={refreshCurrentFindingsPosture}
          />
        )}

        {view === 'uploads' && renderUploads()}
      </div>

      {showUploadModal && (
        <div
          className={styles.uploadModalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reports-upload-modal-title"
          onClick={resetUploadModal}
        >
          <div className={styles.uploadModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.uploadModalHeader}>
              <h3 id="reports-upload-modal-title" className={styles.uploadModalTitle}>
                {t('reports.upload', 'Upload Report')}
              </h3>
              <Button
                type="button"
                size="small"
                color="outline"
                iconOnly
                className={styles.uploadModalCloseBtn}
                onClick={resetUploadModal}
                aria-label={t('common.close', 'Close')}
                icon={<X className="h-5 w-5" aria-hidden />}
              >
                <span className="sr-only">{t('common.close', 'Close')}</span>
              </Button>
            </div>
            {uploadError && (
              <div className={styles.uploadModalError} role="alert">
                {uploadError}
              </div>
            )}
            <div className={styles.uploadModalBody}>
              <div>
                <span className={styles.uploadModalFieldLabel}>{t('reports.uploadFile', 'Select file')}</span>
                <FilePicker
                  accept=".csv,.pdf,.xlsx,.xls"
                  value={uploadFile}
                  onChange={setUploadFile}
                  chooseLabel={t('reports.chooseFile', 'Choose file')}
                  noFileLabel={t('reports.noFileChosen', 'No file chosen')}
                  disabled={uploadStatus !== 'idle'}
                />
              </div>
              <div>
                <span className={styles.uploadModalFieldLabel}>{t('reports.uploadScanner', 'Scanner')}</span>
                <Select<string>
                  className="text-sm"
                  minWidth="100%"
                  options={uploadScannerOptions}
                  value={uploadScanner}
                  onChange={(v) => v != null && setUploadScanner(v)}
                />
              </div>
            </div>
            <div className={styles.uploadModalFooter}>
              <Button type="button" size="small" color="outline" onClick={resetUploadModal}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type="button"
                size="small"
                color="primary"
                disabled={!uploadFile || uploadStatus !== 'idle'}
                onClick={() => handleUploadReport()}
                icon={
                  uploadStatus === 'uploading' ? (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : undefined
                }
              >
                {uploadStatus === 'processing'
                  ? t('reports.messages.report_parsing', 'Parsing report...')
                  : t('reports.startUpload', 'Upload')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default Reports
